import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { GoPlus } from "react-icons/go";
import { useNavigate, Link } from 'react-router-dom';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { ActionButtons } from '../../../Components/Form/ActionButtons/action-buttons.component';
import { Button } from '../../../Components/Form/Button/button.scomponent';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { Table } from '../../../Components/Table/table.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { ListCustomersUseCase } from '../../../Usecase/Customers/list-customers-usecase';
import { useListCustomers } from './customers-list.hook';
import { DestroyCustomersUseCase } from '../../../Usecase/Customers/destroy-customers-usecase';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { Can } from '../../../Components/Can';

interface ICustomersList {
  listCustomersUseCase: ListCustomersUseCase
  destroyCustomersUseCase: DestroyCustomersUseCase
}

const CellText: FC<{ value?: string | null }> = ({ value }) => {
  const text = value?.trim() || '—'

  return (
    <Tooltip title={text !== '—' ? text : ''} placement="top-start" enterDelay={400}>
      <Typography
        variant="body2"
        component="span"
        sx={{
          display: 'block',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.25,
          fontSize: '0.8125rem',
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  )
}

const CustomersList : FC<ICustomersList> = ({ listCustomersUseCase, destroyCustomersUseCase }) => {
    const navigate = useNavigate()

    const {
      loadCustomers,
      list,
      setCustomerToDestroy,
      boxToDestroy,
      customerToDestroyName,
      handleDestroyCustomer,
      totalRegisters,
      loading,
      loadError,
      handleSearch,
      handlePageChange,
      currentSearch,
      page,
    } = useListCustomers({
      listCustomersUseCase,
      destroyCustomersUseCase,
    })

    useEffect(() => {
      loadCustomers({ page: 1, searchBy: '' })
    }, [loadCustomers])

    const columns = useMemo(() => {
        const createColumns: any[] = [
          {
            field: 'name',
            headerName: 'Nome',
            flex: 1.2,
            minWidth: 160,
            sortable: true,
            renderCell: (params: any) => {
              const name = params.row?.name?.trim() || '—'

              return (
                <Tooltip title={name !== '—' ? name : ''} placement="top-start" enterDelay={400}>
                  <Box
                    component={Link}
                    to={`/customers-form/${params.row.id}`}
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      textDecoration: 'none',
                      display: 'block',
                      width: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.25,
                      fontSize: '0.8125rem',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {name}
                  </Box>
                </Tooltip>
              )
            },
          },
          {
            field: 'phone',
            headerName: 'Telefone',
            flex: 0.9,
            minWidth: 120,
            sortable: true,
            renderCell: (params: any) => <CellText value={params.row?.phone} />,
          },
          {
            field: 'email',
            headerName: 'E-mail',
            flex: 1.2,
            minWidth: 180,
            sortable: true,
            renderCell: (params: any) => <CellText value={params.row?.email} />,
          },
          {
            field: 'document',
            headerName: 'Documento',
            flex: 1,
            minWidth: 130,
            sortable: true,
            renderCell: (params: any) => <CellText value={params.row?.document} />,
          },
          {
            field: "actions",
            headerName: "Ações",
            headerAlign: "center",
            align: "center",
            width: 72,
            sortable: false,
            renderCell: (params: any) => (
              <ActionButtons
                params={params}
                setItemToDestroy={setCustomerToDestroy}
                editLink={`/customers-form/${params.row.id}`}
              />
            ),
          }
        ];
      
        return createColumns;
      }, [setCustomerToDestroy]);

    return (
      <Can permissions={['menu:customers:manage']}>
        <BaseScreen>
        <ConfirmDialog
          title='Remover Cliente'
          onCancel={() => setCustomerToDestroy('')}
          description={`Tem certeza que deseja remover "${customerToDestroyName}"?`}
          open={!!boxToDestroy}
          onConfirm={handleDestroyCustomer}
        />
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <BaseContentPage 
                    title='Clientes'
                    subtitle='Gerencie o cadastro de clientes do HUB'
                    breadcrumb={[{ title: 'Clientes', link: "/customers-list"}, { title: 'Listagem'}]}
                    minHeight="auto"
                    search
                    searchDefaultValue={currentSearch}
                    searchFunction={handleSearch}
                    searchLoading={loading && !!list?.length}
                    placeholderSearch='Pesquisar por nome, e-mail ou documento'
                    Action={() => (
                      <Button
                        LeftIcon={() => <GoPlus size={18} />}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 130 }, flexShrink: 0 }}
                        boxLeftSx={{ gap: '10px', marginLeft: '5px' }}
                        textSx={{ textTransform: 'Capitalize', fontWeight: 500, whiteSpace: 'nowrap' }}
                        onClick={() => navigate('/customers-form')}
                      >
                        Adicionar
                      </Button>
                    )}
                >
                  {!!list?.length ? (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Table
                        data={list}
                        totalCount={totalRegisters}
                        columns={columns}
                        search={currentSearch}
                        page={page}
                        onPageChange={handlePageChange}
                        sortingMode="client"
                        rowHeight={52}
                        stickyHeader
                        maxHeight="calc(100vh - 280px)"
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  ) : loading ? (
                    <TableListSkeleton rowHeight={52} />
                  ) : loadError ? (
                    <Placeholder
                      description="Não foi possível carregar os clientes."
                      onRetry={() => loadCustomers({ page: 1, searchBy: currentSearch })}
                    />
                  ) : (
                    <Placeholder description="Nenhum cliente encontrado." />
                  )}
                </BaseContentPage>
            </Paper>
        </BaseScreen>
        </Can>
    )
}

export { CustomersList };
